import {
  format,
  subDays,
  addWeeks,
  subWeeks,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subMonths,
  addMonths,
  startOfQuarter,
  endOfQuarter,
  subQuarters,
  addQuarters,
  startOfYear,
  endOfYear,
  subYears,
  addYears,
} from 'date-fns';

let sepId = 0;
const createSeparator = () => ({
  label: '-----------------',
  value: `-2-${++sepId}`,
  isSeparator: true,
  optionType: 'separator',
});

const getOption = (label, value) => ({
  label,
  value,
  optionType: 'range',
});

export const VIEW_TRANSACTION_OPTIONS = [
  getOption('Range', '3'),
  createSeparator(),
  getOption('Today', '2'),
  getOption('Yesterday', '19'),
  getOption('Recent', '12'),
  getOption('Past 30 Days', '9'),
  createSeparator(),
  getOption('This Week', '1'),
  getOption('This Week-to-Date', '20'),
  getOption('Last Week', '7'),
  getOption('Last Week-to-Date', '13'),
  getOption('Next Week', '22'),
  getOption('Next Four Weeks', '24'),
  createSeparator(),
  getOption('This Month', '5'),
  getOption('This Month-to-Date', '4'),
  getOption('Last Month', '15'),
  getOption('Last Month-to-Date', '18'),
  getOption('Next Month', '23'),
  createSeparator(),
  getOption('This Quarter', '16'),
  getOption('This Quarter-to-Date', '10'),
  getOption('Last Quarter', '17'),
  getOption('Last Quarter-to-Date', '14'),
  getOption('Next Quarter', '25'),
  createSeparator(),
  getOption('This Year', '6'),
  getOption('This Year-to-Date', '11'),
  getOption('Last Year', '8'),
  getOption('Last Year-to-Date', '21'),
  getOption('Next Year', '26'),
];

export const getDateRangeForOption = (optionKey, referenceDate = new Date()) => {
  const today = referenceDate;
  const fmt = (d) => format(d, 'yyyy-MM-dd');

  // Separator clears the date range
  if (!optionKey || optionKey.startsWith('-2') || optionKey.startsWith('separator')) {
    return { fromDate: '', toDate: '' };
  }

  switch (optionKey) {
    case '2':
    case 'today':
      return { fromDate: fmt(today), toDate: fmt(today) };

    case '19':
    case 'yesterday': {
      const y = subDays(today, 1);
      return { fromDate: fmt(y), toDate: fmt(y) };
    }

    case '12':
    case 'recent':
      return { fromDate: fmt(subDays(today, 4)), toDate: fmt(today) };

    case '9':
    case 'past-30-days':
      return { fromDate: fmt(subDays(today, 29)), toDate: fmt(today) };

    case '1':
    case 'this-week':
      return {
        fromDate: fmt(startOfWeek(today, { weekStartsOn: 0 })),
        toDate: fmt(endOfWeek(today, { weekStartsOn: 0 })),
      };

    case '20':
    case 'this-week-to-date':
      return {
        fromDate: fmt(startOfWeek(today, { weekStartsOn: 0 })),
        toDate: fmt(today),
      };

    case '7':
    case 'last-week': {
      const lw = subWeeks(today, 1);
      return {
        fromDate: fmt(startOfWeek(lw, { weekStartsOn: 0 })),
        toDate: fmt(endOfWeek(lw, { weekStartsOn: 0 })),
      };
    }

    case '13':
    case 'last-week-to-date': {
      const lw = subWeeks(today, 1);
      return {
        fromDate: fmt(startOfWeek(lw, { weekStartsOn: 0 })),
        toDate: fmt(lw),
      };
    }

    case '22':
    case 'next-week': {
      const nw = addWeeks(today, 1);
      return {
        fromDate: fmt(startOfWeek(nw, { weekStartsOn: 0 })),
        toDate: fmt(endOfWeek(nw, { weekStartsOn: 0 })),
      };
    }

    case '24':
    case 'next-four-weeks':
      return {
        fromDate: fmt(today),
        toDate: fmt(addWeeks(today, 4)),
      };

    case '5':
    case 'this-month':
      return {
        fromDate: fmt(startOfMonth(today)),
        toDate: fmt(endOfMonth(today)),
      };

    case '4':
    case 'this-month-to-date':
      return {
        fromDate: fmt(startOfMonth(today)),
        toDate: fmt(today),
      };

    case '15':
    case 'last-month': {
      const lm = subMonths(today, 1);
      return {
        fromDate: fmt(startOfMonth(lm)),
        toDate: fmt(endOfMonth(lm)),
      };
    }

    case '18':
    case 'last-month-to-date': {
      const lm = subMonths(today, 1);
      return {
        fromDate: fmt(startOfMonth(lm)),
        toDate: fmt(lm),
      };
    }

    case '23':
    case 'next-month': {
      const nm = addMonths(today, 1);
      return {
        fromDate: fmt(startOfMonth(nm)),
        toDate: fmt(endOfMonth(nm)),
      };
    }

    case '16':
    case 'this-quarter':
      return {
        fromDate: fmt(startOfQuarter(today)),
        toDate: fmt(endOfQuarter(today)),
      };

    case '10':
    case 'this-quarter-to-date':
      return {
        fromDate: fmt(startOfQuarter(today)),
        toDate: fmt(today),
      };

    case '17':
    case 'last-quarter': {
      const lq = subQuarters(today, 1);
      return {
        fromDate: fmt(startOfQuarter(lq)),
        toDate: fmt(endOfQuarter(lq)),
      };
    }

    case '14':
    case 'last-quarter-to-date': {
      const lq = subQuarters(today, 1);
      return {
        fromDate: fmt(startOfQuarter(lq)),
        toDate: fmt(lq),
      };
    }

    case '25':
    case 'next-quarter': {
      const nq = addQuarters(today, 1);
      return {
        fromDate: fmt(startOfQuarter(nq)),
        toDate: fmt(endOfQuarter(nq)),
      };
    }

    case '6':
    case 'this-year':
      return {
        fromDate: fmt(startOfYear(today)),
        toDate: fmt(endOfYear(today)),
      };

    case '11':
    case 'this-year-to-date':
      return {
        fromDate: fmt(startOfYear(today)),
        toDate: fmt(today),
      };

    case '8':
    case 'last-year': {
      const ly = subYears(today, 1);
      return {
        fromDate: fmt(startOfYear(ly)),
        toDate: fmt(endOfYear(ly)),
      };
    }

    case '21':
    case 'last-year-to-date': {
      const ly = subYears(today, 1);
      return {
        fromDate: fmt(startOfYear(ly)),
        toDate: fmt(ly),
      };
    }

    case '26':
    case 'next-year': {
      const ny = addYears(today, 1);
      return {
        fromDate: fmt(startOfYear(ny)),
        toDate: fmt(endOfYear(ny)),
      };
    }

    case '3':
    case 'range':
    default:
      return null;
  }
};

/**
 * Maps PeriodStartDate and PeriodEndDate from CorporationDetails API response
 * to fromDate and toDate in 'yyyy-MM-dd' format.
 *
 * Example input:
 *   PeriodStartDate: "01/01", PeriodEndDate: "12/31"
 * Output:
 *   { fromDate: "2026-01-01", toDate: "2026-12-31" }
 */
export const mapCorporationPeriodDates = (periodStartDate, periodEndDate) => {
  if (!periodStartDate) return null;
  const currentYear = new Date().getFullYear();

  const parseDatePart = (str, defaultMonth, defaultDay) => {
    if (!str) return { month: defaultMonth, day: defaultDay, year: currentYear };
    const trimmed = String(str).trim();
    if (trimmed.includes('-')) {
      const parts = trimmed.split('-').map(Number);
      if (parts.length === 3) return { year: parts[0], month: parts[1], day: parts[2] };
    }
    const parts = trimmed.split('/').map(Number);
    if (parts.length === 3) {
      return { month: parts[0], day: parts[1], year: parts[2] };
    }
    if (parts.length === 2) {
      return { month: parts[0], day: parts[1], year: currentYear };
    }
    return { month: defaultMonth, day: defaultDay, year: currentYear };
  };

  const start = parseDatePart(periodStartDate, 1, 1);
  const end = parseDatePart(periodEndDate, 12, 31);

  let startYear = start.year;
  let endYear = end.year;

  // If MM/DD format without explicit year
  if (!periodStartDate.includes('/') || periodStartDate.split('/').length < 3) {
    if (start.month > end.month) {
      const today = new Date();
      const thisYearStart = new Date(currentYear, start.month - 1, start.day);
      if (today < thisYearStart) {
        startYear = currentYear - 1;
        endYear = currentYear;
      } else {
        startYear = currentYear;
        endYear = currentYear + 1;
      }
    } else {
      startYear = currentYear;
      endYear = currentYear;
    }
  }

  const pad = (n) => String(n).padStart(2, '0');
  const fromDate = `${startYear}-${pad(start.month)}-${pad(start.day)}`;
  const toDate = `${endYear}-${pad(end.month)}-${pad(end.day)}`;

  return { fromDate, toDate };
};
